// Shell-level handlers + derived counts that the ActivityFloatingDock
// (and a couple of other top-bar surfaces) need in order to navigate
// out to Ads Assistant / Sales Console panels and to badge urgent
// follow-ups on the daily-checklist dock section.
//
// Extracted in Slice C of
// docs/planning/activity_dock_workflow_facilitation.md §6 so that
// `LeadWorkspaceShell.tsx` doesn't have to grow past the file-size
// hygiene SOFT threshold (260 lines) just to wire one more dock
// section. Grouping all the dock-bound navigation handlers in one
// place also makes the next slice (D — Unsent leads → dock) a tiny
// addition rather than another bespoke set of callbacks at the
// shell level.

import { useCallback, useMemo } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { Lead } from "@/types/lead";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { AdsAssistantPanelIntent } from "@/components/ads-assistant/adsAssistantPanelUtils";
import { countFollowUpsNeedingAttention } from "@/lib/lead/followUpAttention";
import { queueBuilderAssistantSession } from "@/lib/manufacturing/builderAssistantOpenSession";

export type LeadWorkspaceShellDockHandlersInput = {
  setMode: (m: PrimaryMode) => void;
  setAdsPanelIntent: Dispatch<SetStateAction<AdsAssistantPanelIntent>>;
  locationScopedLeads: Lead[];
  /** Opens Knowledge Base with gap inbox sub-mode intent (SC-5). */
  onOpenKbGapInbox?: () => void;
};

export type LeadWorkspaceShellDockHandlers = {
  /** TopStrip "Ad alerts" CTA and dock click-to-nav for ad ingestion. */
  handleOpenAdsIngestion: () => void;
  /** Sidebar Location Links + Ads Activity card "Open library" CTA. */
  handleOpenAdsLibrary: () => void;
  /** Dock daily-checklist Ads Assistant inline CTA (defaults to ingestion). */
  handleOpenAdsAssistant: () => void;
  /** Dock daily-checklist Ads Assistant suggestion CTA. */
  handleOpenAdsAssistantSuggestion: () => void;
  /** Dock daily-checklist follow-up alert + future Sales Console nav links. */
  handleOpenSalesConsole: () => void;
  /** Count of open follow-ups due today or overdue across the workspace. */
  followUpAttentionCount: number;
  /** Manager bucket — AI Prompting mode for suggestion review. */
  handleOpenAiPrompting: () => void;
  handleOpenKbGapInbox: () => void;
  handleOpenAdmin: () => void;
  handleOpenDashboard: () => void;
  handleOpenMfgInventory: () => void;
  handleOpenMfgPurchasing: () => void;
  handleOpenMfgBuilderAssistant: (sessionId?: string, categoryId?: string | null) => void;
};

export function useLeadWorkspaceShellDockHandlers(
  input: LeadWorkspaceShellDockHandlersInput,
): LeadWorkspaceShellDockHandlers {
  const { setMode, setAdsPanelIntent, locationScopedLeads, onOpenKbGapInbox } =
    input;

  const handleOpenAdsIngestion = useCallback(() => {
    setAdsPanelIntent({ area: "ingestion" });
    setMode("adsAssistant");
  }, [setAdsPanelIntent, setMode]);

  // Library was re-homed to the Manager-bucket `adsGuidance` mode on
  // 2026-06-15 (see docs/design/admin_split_and_entitlements.md §7.6).
  // The dock CTA + sidebar Location-Links "Open library" links keep
  // their entry-point name (`handleOpenAdsLibrary`) for source-call-site
  // stability and just retarget to the new mode. `setAdsPanelIntent`
  // is no longer passed (no sub-area to force), so the Ads Assistant
  // panel resets to its default ingestion view if someone later
  // navigates back to it.
  const handleOpenAdsLibrary = useCallback(() => {
    setMode("adsGuidance");
  }, [setMode]);

  // Currently mirrors `handleOpenAdsIngestion` because the daily
  // checklist's row CTAs originally pointed at ingestion-as-default
  // (see `LeadWorkspaceShellModes.tsx` history). Kept as a separate
  // handler so it can re-aim independently later if the dock copy
  // diverges from the Leads-mode wording.
  const handleOpenAdsAssistant = useCallback(() => {
    setAdsPanelIntent({ area: "ingestion" });
    setMode("adsAssistant");
  }, [setAdsPanelIntent, setMode]);

  const handleOpenAdsAssistantSuggestion = useCallback(() => {
    setAdsPanelIntent({ area: "suggestion" });
    setMode("adsAssistant");
  }, [setAdsPanelIntent, setMode]);

  const handleOpenSalesConsole = useCallback(() => {
    setMode("salesConsole");
  }, [setMode]);

  const handleOpenAiPrompting = useCallback(() => {
    setMode("aiPrompting");
  }, [setMode]);

  const handleOpenKbGapInbox = useCallback(() => {
    onOpenKbGapInbox?.();
  }, [onOpenKbGapInbox]);

  const handleOpenAdmin = useCallback(() => {
    setMode("admin");
  }, [setMode]);

  const handleOpenDashboard = useCallback(() => {
    setMode("dashboard");
  }, [setMode]);

  const handleOpenMfgInventory = useCallback(() => {
    setMode("mfgInventory");
  }, [setMode]);

  const handleOpenMfgPurchasing = useCallback(() => {
    setMode("mfgPurchasing");
  }, [setMode]);

  const handleOpenMfgBuilderAssistant = useCallback((sessionId?: string, categoryId?: string | null) => {
    setMode("mfgBuilderAssistant");
    if (sessionId) queueBuilderAssistantSession(sessionId, categoryId);
  }, [setMode]);

  const followUpAttentionCount = useMemo(
    () => countFollowUpsNeedingAttention(locationScopedLeads),
    [locationScopedLeads],
  );

  return {
    handleOpenAdsIngestion,
    handleOpenAdsLibrary,
    handleOpenAdsAssistant,
    handleOpenAdsAssistantSuggestion,
    handleOpenSalesConsole,
    followUpAttentionCount,
    handleOpenAiPrompting,
    handleOpenKbGapInbox,
    handleOpenAdmin,
    handleOpenDashboard,
    handleOpenMfgInventory,
    handleOpenMfgPurchasing,
    handleOpenMfgBuilderAssistant,
  };
}
